import '../zod-runtime.js';
import * as z from 'zod';
import { isLocale, LOCALES } from '../i18n/index.js';
import { isCountryCode } from '../locale/countries.js';
import { isValidPhone } from '../locale/phone.js';
/**
 * The primitives every entity schema is built from.
 *
 * Two conventions hold throughout and both are enforced rather than remembered.
 *
 * **Every object schema is strict.** An unknown key is rejected, never ignored. A silently dropped
 * key is how a client believes it saved something it did not, and how a renamed field keeps
 * appearing to work for months. `strict()` below is the only object constructor these schemas use.
 *
 * **A refusal names a message key, never the value.** Zod's default messages quote what was
 * submitted; here every custom message is a catalogue key, so the value stays out of the error
 * payload and out of the logs, and the client renders the message in the reader's language.
 */
/** The one object constructor these schemas use: unknown keys are an error, never a shrug. */
export const strict = z.strictObject;
/**
 * The body of an unsafe route that takes none, declared rather than omitted.
 *
 * It is `strict({})` rather than nothing, and the difference is not cosmetic. Express 5 leaves
 * `request.body` UNDEFINED when no parser matched the content type, so a `POST` sent with no body
 * and no `Content-Type` reaches the validator as `undefined` and is refused — which means every
 * bodyless write in this product needs its client to send `{}` and say that it is JSON. Declaring
 * the schema is what tells a client that, and what puts a `requestBody` into the OpenAPI document
 * for it to read.
 *
 * Three route modules in the server declare the same shape locally today. They should converge on
 * this one; until they do, the OpenAPI document is what keeps all four in step: the browser's route
 * table is checked against it for the request body AND the answer, so neither half can drift.
 * (Every route the DOCUMENT declares and the table does not is simply a route the browser does not
 * call yet, which is the ordinary state of a front end being built.)
 */
export const emptyRequestSchema = strict({});
/**
 * The query of a route that takes none, declared rather than omitted.
 *
 * Omitting the section is not the same statement: the validator skips a section no schema was
 * declared for, so an unknown query parameter on such a route is accepted and silently dropped.
 * That is the "the field validates and nothing happens" shape this API refuses everywhere else, and
 * it is the difference between a client learning it misspelled `?statuss=` and a client believing
 * its filter was applied. Declaring an empty strict object makes the absence a promise.
 */
export const noQuerySchema = strict({});
/** An application-generated UUID (v7 in practice; the version is not the contract). */
export const uuidSchema = z.uuid({ error: 'errors.validation.invalid_format' });
/** `P-000123`: the quotable record id an operator reads out over the radio. */
export const shortRefSchema = z
    .string()
    .regex(/^P-\d{6,9}$/u, { error: 'errors.validation.invalid_format' });
/** A calendar date with no time and no zone, which is what a date of birth is. */
export const isoDateSchema = z.iso.date({ error: 'errors.validation.invalid_date' });
/** An instant, always stored and transmitted in UTC. */
export const isoDateTimeSchema = z.iso.datetime({ error: 'errors.validation.invalid_date' });
/** One of the two languages this product ships. */
export const localeSchema = z.enum(LOCALES);
/** An ISO 3166-1 alpha-2 code this product accepts, upper case. */
export const countryCodeSchema = z
    .string()
    .refine(isCountryCode, { error: 'errors.validation.invalid_country' });
/** A telephone number in any written form, validated against the real numbering plans. */
export const phoneSchema = z
    .string()
    .refine((value) => isValidPhone(value), { error: 'errors.validation.invalid_phone' });
/** An email address. Length-capped, because the column is finite and so is every mail server. */
export const emailSchema = z.email({ error: 'errors.validation.invalid_email' }).max(320);
/** The optimistic-concurrency counter. A stale value is a 409, never a silent overwrite. */
export const versionSchema = z.int().nonnegative();
/** A machine key: lower snake case, which is what a field key and a settings key both are. */
/**
 * How a record action was reached (AD-53).
 *
 * The owner's requirement is that any of the record actions reached by SEARCH rather than by a scan
 * is marked, and that the manual-versus-scan ratio per operator is a dashboard metric — a ratio
 * that means nothing unless every action carries one of these.
 *
 * **It is a strict subset of `presenceMethodSchema`, and a test pins that.** The presence enum is
 * the wider one: it also carries the machine methods (`api`, `system`, `reconciliation`,
 * `manual_backfill`), which describe a movement written by something other than a person at a
 * terminal and are meaningless on a record action. It lives here rather than beside that enum
 * because `presence.ts` imports `person.ts`, which imports `credential.ts`, so a credential route
 * reaching for the presence enum would close an import cycle — and an ESM cycle does not fail
 * loudly, it leaves one of the two schemas `undefined` at evaluation time.
 */
export const recordActionMethodSchema = z.enum([
    'scan_hardware',
    'scan_camera',
    'scan_upload',
    'manual_key',
    'manual_search',
]);
/**
 * What a record action reached WITHOUT a scan is recorded as.
 *
 * The default rather than a scan value, and the direction matters: an action whose caller said
 * nothing about how it got there was not reached by a scanner, because a scanner-driven client
 * knows which of the three scan methods it used and says so. Defaulting the other way would
 * understate exactly the number AD-53 exists to produce.
 */
export const DEFAULT_RECORD_ACTION_METHOD = 'manual_search';
/**
 * A stable identifier the product compares and stores: a field key, a choice's value, a column name.
 *
 * The shape refusal carries its own message rather than the generic format one, because "the format
 * is not valid" is unactionable against a box somebody has just typed a word into; the grammar is in
 * the sentence rather than in a placeholder, since a regular expression in an error body is
 * reflected schema detail and a string no translator could move. The two bounds declare nothing:
 * `errors/details.ts` reads the issue's origin, so `min(1)` resolves to "required" and `max(64)` to
 * the character-counting sentence, each carrying its own number.
 */
export const machineKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/u, { error: 'errors.validation.invalid_key_format' });
/** A short label in both languages. Neither may be blank: a half-translated label is a defect. */
export const bilingualTextSchema = strict({
    es: z.string().trim().min(1).max(120),
    en: z.string().trim().min(1).max(120),
});
/** A longer help text in both languages, optional as a pair rather than one language at a time. */
export const bilingualHelpSchema = strict({
    es: z.string().trim().max(400),
    en: z.string().trim().max(400),
});
/** Who did it and when, on every response that carries a record. */
export const auditStampSchema = strict({
    createdAt: isoDateTimeSchema,
    createdBy: uuidSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: uuidSchema.nullable(),
});
/** The idempotency key a mutation carries (AD-30). Generated by the client, opaque to the server. */
export const idempotencyKeySchema = z.uuid({
    error: 'errors.validation.idempotency_key_required',
});
/**
 * Why something a domain RECORDED nevertheless refused what was asked, on a 200.
 *
 * **Two domains answer this shape and it is declared once.** The gate records a movement row for a
 * blocked or archived person and then has to say why they are still standing at the door; the
 * counter records a meal row for an archived record and has to say the same. Both are outcomes
 * rather than errors, because a row WAS written and a client has something to count.
 *
 * A code and nothing else, because AD-35 makes the code the thing a client branches on and the
 * message the thing it renders from its own catalogue. It is a nested object rather than a bare
 * string so that the shape matches the error body a client already parses, and so that a later
 * release can carry a detail beside it without changing the member's type.
 */
export const recordedRefusalSchema = strict({
    code: z.string().min(1).max(64),
});
/** A reason code: a short machine token from a fixed list the caller was given. */
export const reasonCodeSchema = z
    .string()
    .min(1)
    .max(48)
    .regex(/^[a-z][a-z0-9_]*$/u, { error: 'errors.validation.invalid_option' });
/** Narrows a request's `Accept-Language`-derived locale, defaulting rather than failing. */
export function localeOr(value, fallback) {
    return value !== undefined && isLocale(value) ? value : fallback;
}
//# sourceMappingURL=common.js.map